import React, { useState } from "react";
import toast from "react-hot-toast";
import StaticContentPage from "./StaticContentPage";
import { contactUsText } from "../data/staticPagesContent";
import { usePostContactUsMutation } from "../store/api.ts";

const ContactUs = () => {
  const [postContactUs, { isLoading }] = usePostContactUsMutation();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  const [errors, setErrors] = useState({});

  const validateEmail = (email) => {
    const trimmedEmail = email.trim();

    if (!trimmedEmail) {
      return "Email is required.";
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

    if (!emailRegex.test(trimmedEmail)) {
      return "Please enter a valid email address.";
    }

    return "";
  };

  const validatePhone = (phone) => {
    const trimmedPhone = phone.trim();

    if (!trimmedPhone) {
      return "Phone number is required.";
    }

    const phoneRegex = /^\+?[0-9\s().-]{7,20}$/;

    if (!phoneRegex.test(trimmedPhone)) {
      return "Please enter a valid phone number.";
    }

    const digitsOnly = trimmedPhone.replace(/\D/g, "");

    if (digitsOnly.length < 7 || digitsOnly.length > 15) {
      return "Phone number must contain 7 to 15 digits.";
    }

    return "";
  };

  const validateForm = () => {
    const nextErrors = {
      email: validateEmail(formData.email),
      phone: validatePhone(formData.phone),
    };

    Object.keys(nextErrors).forEach((key) => {
      if (!nextErrors[key]) {
        delete nextErrors[key];
      }
    });

    setErrors(nextErrors);

    return Object.keys(nextErrors).length === 0;
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => {
      const nextErrors = { ...prev };

      if (name === "email") {
        const emailError = validateEmail(value);

        if (emailError) {
          nextErrors.email = emailError;
        } else {
          delete nextErrors.email;
        }
      }

      if (name === "phone") {
        const phoneError = validatePhone(value);

        if (phoneError) {
          nextErrors.phone = phoneError;
        } else {
          delete nextErrors.phone;
        }
      }

      return nextErrors;
    });
  };

  const getErrorMessage = (error) => {
    if (typeof error?.data?.detail === "string") return error.data.detail;
    if (typeof error?.data?.message === "string") return error.data.message;
    if (typeof error?.error === "string") return error.error;

    if (error?.data && typeof error.data === "object") {
      const firstError = Object.values(error.data)?.[0];

      if (Array.isArray(firstError)) return firstError[0];
      if (typeof firstError === "string") return firstError;
    }

    return "Failed to send your message. Please try again.";
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const isValid = validateForm();

    if (!isValid) {
      toast.error("Please fix the highlighted fields.");
      return;
    }

    try {
      await postContactUs({
        contactUsLeadCreate: {
          name: formData.name.trim(),
          email: formData.email.trim(),
          phone_number: formData.phone.trim(),
          message: formData.message.trim(),
        },
      }).unwrap();

      toast.success("Your message has been sent successfully.");

      setFormData({
        name: "",
        email: "",
        phone: "",
        message: "",
      });

      setErrors({});
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  return (
    <StaticContentPage
      title="Contact Us"
      seoTitle="Contact Us | Eveness Support"
      seoDescription="Contact Eveness for customer support, order help, product questions, returns, payments, and general inquiries."
      canonicalPath="/contact-us"
    >
      <div className="whitespace-pre-line">{contactUsText}</div>

      <form
        onSubmit={handleSubmit}
        className="mt-10 grid grid-cols-1 gap-5 max-w-[720px]"
        noValidate
      >
        <div>
          <label
            htmlFor="name"
            className="block text-sm font-medium text-[#22272A] mb-2"
          >
            Name
          </label>
          <input
            id="name"
            name="name"
            type="text"
            value={formData.name}
            onChange={handleChange}
            required
            className="w-full rounded-xl border border-black/10 bg-white/70 px-4 py-3 outline-none focus:border-[#22272A] transition-colors"
            placeholder="Enter your name"
          />
        </div>

        <div>
          <label
            htmlFor="email"
            className="block text-sm font-medium text-[#22272A] mb-2"
          >
            E-mail
          </label>
          <input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            required
            aria-invalid={Boolean(errors.email)}
            className={`w-full rounded-xl border bg-white/70 px-4 py-3 outline-none transition-colors ${
              errors.email
                ? "border-red-500 focus:border-red-500"
                : "border-black/10 focus:border-[#22272A]"
            }`}
            placeholder="Enter your email"
          />
          {errors.email ? (
            <p className="mt-1 text-sm text-red-600">{errors.email}</p>
          ) : null}
        </div>

        <div>
          <label
            htmlFor="phone"
            className="block text-sm font-medium text-[#22272A] mb-2"
          >
            Phone Number
          </label>
          <input
            id="phone"
            name="phone"
            type="tel"
            value={formData.phone}
            onChange={handleChange}
            required
            aria-invalid={Boolean(errors.phone)}
            className={`w-full rounded-xl border bg-white/70 px-4 py-3 outline-none transition-colors ${
              errors.phone
                ? "border-red-500 focus:border-red-500"
                : "border-black/10 focus:border-[#22272A]"
            }`}
            placeholder="Enter your phone number"
          />
          {errors.phone ? (
            <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
          ) : null}
        </div>

        <div>
          <label
            htmlFor="message"
            className="block text-sm font-medium text-[#22272A] mb-2"
          >
            Message
          </label>
          <textarea
            id="message"
            name="message"
            rows={6}
            value={formData.message}
            onChange={handleChange}
            required
            className="w-full resize-none rounded-xl border border-black/10 bg-white/70 px-4 py-3 outline-none focus:border-[#22272A] transition-colors"
            placeholder="Write your message"
          />
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="w-full sm:w-fit rounded-full bg-[#22272A] text-white px-8 py-3 text-sm font-medium hover:bg-black transition-colors disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isLoading ? "Sending..." : "Submit"}
        </button>
      </form>
    </StaticContentPage>
  );
};

export default ContactUs;
